import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const EMPTY_VITALS = {
  temperature: "",
  blood_pressure: "",
  heart_rate: "",
  respiratory_rate: "",
  oxygen_saturation: "",
  weight: "",
  height: "",
  bmi: "",
};

function calculateBmi(weight, height) {
  const weightKg = Number(weight);
  const heightCm = Number(height);

  if (!weightKg || !heightCm || weightKg <= 0 || heightCm <= 0) {
    return "";
  }

  const heightM = heightCm / 100;
  return (weightKg / (heightM * heightM)).toFixed(1);
}

export function vitalsSummary(v) {
  if (!v) return "";
  const parts = [];
  if (v.blood_pressure) parts.push(`BP ${v.blood_pressure}`);
  if (v.temperature) parts.push(`T ${v.temperature}`);
  if (v.heart_rate) parts.push(`HR ${v.heart_rate}`);
  if (v.respiratory_rate) parts.push(`RR ${v.respiratory_rate}`);
  if (v.oxygen_saturation) parts.push(`SpO2 ${v.oxygen_saturation}`);
  if (v.weight) parts.push(`Wt ${v.weight}`);
  if (v.height) parts.push(`Ht ${v.height}`);
  if (v.bmi) parts.push(`BMI ${v.bmi}`);
  return parts.join(" · ");
}

export function hasAnyVital(v) {
  return !!(
    v &&
    (
      v.temperature ||
      v.blood_pressure ||
      v.heart_rate ||
      v.respiratory_rate ||
      v.oxygen_saturation ||
      v.weight ||
      v.height ||
      v.bmi
    )
  );
}

export default function VitalsForm({ value, onChange, idPrefix = "vitals" }) {
  const v = { ...EMPTY_VITALS, ...(value || {}) };

  const computedBmi = useMemo(() => calculateBmi(v.weight, v.height), [v.weight, v.height]);

  const set = (key, val) => {
    const next = { ...v, [key]: val };

    if (key === "weight" || key === "height") {
      next.bmi = calculateBmi(
        key === "weight" ? val : next.weight,
        key === "height" ? val : next.height
      );
    }

    onChange(next);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <Label>Temperature (°C)</Label>
        <Input
          value={v.temperature || ""}
          onChange={(e) => set("temperature", e.target.value)}
          placeholder="36.8"
          data-testid={`${idPrefix}-temperature`}
        />
      </div>

      <div>
        <Label>Blood Pressure</Label>
        <Input
          value={v.blood_pressure || ""}
          onChange={(e) => set("blood_pressure", e.target.value)}
          placeholder="120/80"
          data-testid={`${idPrefix}-blood-pressure`}
        />
      </div>

      <div>
        <Label>Heart Rate (bpm)</Label>
        <Input
          value={v.heart_rate || ""}
          onChange={(e) => set("heart_rate", e.target.value)}
          placeholder="72"
          data-testid={`${idPrefix}-heart-rate`}
        />
      </div>

      <div>
        <Label>Respiratory Rate</Label>
        <Input
          value={v.respiratory_rate || ""}
          onChange={(e) => set("respiratory_rate", e.target.value)}
          placeholder="16"
          data-testid={`${idPrefix}-respiratory-rate`}
        />
      </div>

      <div>
        <Label>O₂ Saturation (%)</Label>
        <Input
          value={v.oxygen_saturation || ""}
          onChange={(e) => set("oxygen_saturation", e.target.value)}
          placeholder="98"
          data-testid={`${idPrefix}-oxygen-saturation`}
        />
      </div>

      <div>
        <Label>Weight (kg)</Label>
        <Input
          value={v.weight || ""}
          onChange={(e) => set("weight", e.target.value)}
          placeholder="65"
          data-testid={`${idPrefix}-weight`}
        />
      </div>

      <div>
        <Label>Height (cm)</Label>
        <Input
          value={v.height || ""}
          onChange={(e) => set("height", e.target.value)}
          placeholder="165"
          data-testid={`${idPrefix}-height`}
        />
      </div>

      <div>
        <Label>BMI</Label>
        <Input
          value={v.bmi || computedBmi || ""}
          readOnly
          placeholder="auto"
          data-testid={`${idPrefix}-bmi`}
        />
      </div>
    </div>
  );
}
